import type { CreateRecoveryRequest, ReleasePolicy, RecoveryDto } from '@gsls/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorNote,
  Field,
  InfoNote,
  PageHeader,
  Spinner,
  Tooltip,
  inputClass,
} from '../components/ui';
import { DataTable, type DataColumn } from '../components/DataTable';
import { FilterBar } from '../components/FilterBar';
import { MultiCheckboxList } from '../components/MultiCheckboxList';
import { StickyActionBar } from '../components/StickyActionBar';
import { ApiError, api } from '../lib/api';
import { formatDateTime, formatQuantity, formatUnitCost, humanise } from '../lib/format';
import { useDeepLink } from '../lib/useDeepLink';
import { Can } from '../lib/session';

const RECOVERY_COLUMNS: DataColumn<RecoveryDto>[] = [
  { key: 'code', header: 'Recovery', cell: (recovery) => (
    <span className="font-mono text-xs">{recovery.code}</span>
  ) },
  {
    key: 'material',
    header: 'Material',
    primary: true,
    cell: (recovery) => (
      <>
        <span className="font-mono text-xs">{recovery.itemCode}</span>
        <br />
        <span className="text-ink-body">{recovery.itemName}</span>
      </>
    ),
  },
  {
    key: 'quantity',
    header: 'Quantity',
    align: 'right',
    cell: (recovery) => (
      <span className="whitespace-nowrap font-semibold text-ink">
        {formatQuantity(recovery.quantity, recovery.uom)}
      </span>
    ),
  },
  {
    key: 'lot',
    header: 'Recovered lot',
    cell: (recovery) => (
      <>
        <span className="font-mono text-xs">{recovery.lotCode}</span>
        <br />
        <Badge tone={recovery.lotStatus === 'available' ? 'green' : 'hazard'}>
          {humanise(recovery.lotStatus)}
        </Badge>
      </>
    ),
  },
  {
    key: 'source',
    header: 'Source',
    cell: (recovery) => (
      <span className="text-xs">
        {recovery.batchCode ? (
          <>
            batch <span className="font-mono">{recovery.batchCode}</span>
          </>
        ) : (
          'standalone'
        )}
        <br />
        <span className="text-ink-muted">
          {recovery.sourceLots.map((lot) => lot.lotCode).join(', ')}
        </span>
      </span>
    ),
  },
  {
    key: 'credit',
    header: 'Credit',
    cell: (recovery) => (
      <span className="whitespace-nowrap text-xs">
        {formatUnitCost(recovery.creditedUnitCost, recovery.uom)}
        <br />
        <span className="text-ink-muted">{humanise(recovery.creditPolicy)}</span>
      </span>
    ),
  },
  {
    key: 'release',
    header: 'Release',
    cell: (recovery) => (
      <>
        <Badge tone={recovery.releasePolicy === 'immediate' ? 'green' : 'hazard'}>
          {humanise(recovery.releasePolicy)}
        </Badge>
        {recovery.policyOverridden ? (
          <span className="ml-1 text-xs text-accent">overridden</span>
        ) : null}
      </>
    ),
  },
  {
    key: 'when',
    header: 'When',
    cell: (recovery) => (
      <span className="whitespace-nowrap text-xs">{formatDateTime(recovery.occurredAt)}</span>
    ),
  },
];

/**
 * Recovery — production material going back into inventory.
 *
 * Recovered material is ordinary stock that carries a `recovered` tag and points
 * at where it came from. There is no separate store for it.
 */
export function RecoveryPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [defaultBatchId, setDefaultBatchId] = useState<string | undefined>(undefined);

  /** The dashboard opens the recovery form directly, optionally for a batch. */
  useDeepLink('action', (value) => {
    if (value === 'new') setFormOpen(true);
  });
  useDeepLink('batch', (value) => setDefaultBatchId(value));

  const recoveries = useQuery({
    queryKey: ['recoveries', { search }],
    queryFn: () => api.recoveries.list({ search, limit: 100 }),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['recoveries'] });
    void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    void queryClient.invalidateQueries({ queryKey: ['quality'] });
  }

  return (
    <>
      <PageHeader
        eyebrow="Production"
        title="Recovered material"
        description="Material reclaimed from a batch (or from stock directly) and put back into inventory. It is ordinary stock carrying a recovered tag, valued per the item's credit policy, and held for QC release if the item says so."
        actions={
          <Can capability="recovery.record">
            <Button onClick={() => setFormOpen(true)}>Record recovery</Button>
          </Can>
        }
      />

      {formOpen ? (
        <RecoveryForm
          defaultBatchId={defaultBatchId}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            invalidate();
            setFormOpen(false);
          }}
        />
      ) : null}

      <Card>
        <CardBody>
          <FilterBar activeCount={search ? 1 : 0}>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <input
                name="recoverySearch"
                className={`${inputClass} max-w-xs`}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search code, item or step"
                aria-label="Search recoveries"
              />
              {recoveries.isSuccess ? (
                <span className="text-sm text-ink-muted">
                  {recoveries.data.total} recover{recoveries.data.total === 1 ? 'y' : 'ies'}
                </span>
              ) : null}
            </div>
          </FilterBar>

          {recoveries.isPending ? <Spinner /> : null}
          <ErrorNote>
            {recoveries.isError ? 'Could not load recoveries. Is the API running?' : ''}
          </ErrorNote>

          {recoveries.isSuccess ? (
            <DataTable
              rows={recoveries.data.items}
              columns={RECOVERY_COLUMNS}
              rowKey={(recovery) => recovery.id}
              empty="Nothing recovered yet. Recovered material appears here and in inventory like any other stock."
            />
          ) : null}
        </CardBody>
      </Card>

      <StickyActionBar
        actions={[{ label: 'Record recovery', onClick: () => setFormOpen(true), capability: 'recovery.record', tone: 'primary' }]}
        hidden={formOpen}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Recording                                                                   */
/* -------------------------------------------------------------------------- */

function RecoveryForm({
  onClose,
  onSaved,
  defaultBatchId,
}: {
  onClose: () => void;
  onSaved: () => void;
  defaultBatchId?: string;
}) {
  const [batchId, setBatchId] = useState(defaultBatchId ?? '');
  const [itemId, setItemId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [uom, setUom] = useState('');
  const [recoveryStep, setRecoveryStep] = useState('');
  const [method, setMethod] = useState('');
  const [grade, setGrade] = useState('');
  const [purityPct, setPurityPct] = useState('');
  const [sourceLotIds, setSourceLotIds] = useState<string[]>([]);
  const [releasePolicy, setReleasePolicy] = useState<'' | ReleasePolicy>('');
  const [policyOverrideReason, setPolicyOverrideReason] = useState('');
  const [creditedUnitCost, setCreditedUnitCost] = useState('');

  const batches = useQuery({
    queryKey: ['batches', { limit: 100 }],
    queryFn: () => api.batches.list({ limit: 100 }),
  });
  const items = useQuery({
    queryKey: ['items', { limit: 200 }],
    queryFn: () => api.items.list({ limit: 200 }),
  });
  const locations = useQuery({
    queryKey: ['locations', { limit: 200 }],
    queryFn: () => api.locations.list({ limit: 200 }),
  });

  const selectedBatch = batches.data?.items.find((batch) => batch.id === batchId) ?? null;
  const selectedItem = items.data?.items.find((item) => item.id === itemId) ?? null;

  // Lots of the selected material. When a batch is chosen, only the lots that
  // batch actually drew from are offered — the API verifies this too.
  const candidateLots = useQuery({
    queryKey: ['inventory', 'lots', { itemId }],
    queryFn: () => api.inventory.lots({ itemId, includeEmpty: true, limit: 200 }),
    enabled: Boolean(itemId),
  });

  const lotsInScope = (candidateLots.data?.items ?? []).filter((lot) =>
    selectedBatch
      ? selectedBatch.consumptions?.some((consumption) => consumption.lotId === lot.id)
      : true,
  );

  const defaultPolicy = selectedItem?.recoveredHandling ?? 'quarantine';
  const effectivePolicy: ReleasePolicy = releasePolicy === '' ? defaultPolicy : releasePolicy;
  const isOverride = releasePolicy !== '' && releasePolicy !== defaultPolicy;

  const record = useMutation({
    mutationFn: () =>
      api.recoveries.create({
        itemId,
        ...(batchId ? { batchId } : {}),
        locationId,
        quantity: quantity.trim(),
        uom,
        recoveryStep: recoveryStep.trim(),
        ...(method.trim() ? { method: method.trim() } : {}),
        ...(grade.trim() ? { grade: grade.trim() } : {}),
        ...(purityPct.trim() ? { purityPct: purityPct.trim() } : {}),
        sourceLotIds,
        ...(releasePolicy !== '' ? { releasePolicy } : {}),
        ...(isOverride && policyOverrideReason.trim()
          ? { policyOverrideReason: policyOverrideReason.trim() }
          : {}),
        ...(creditedUnitCost.trim() ? { creditedUnitCost: creditedUnitCost.trim() } : {}),
      } satisfies CreateRecoveryRequest),
    onSuccess: onSaved,
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    record.mutate();
  }

  return (
    <Card className="mb-6">
      <CardBody>
        <h2 className="text-base font-bold text-ink">Record a recovery</h2>
        <p className="mt-1 text-sm font-light text-ink-body">
          The material is valued per the item's credit policy and released according to its policy,
          or according to what you choose here.
        </p>

        <form
          autoComplete="off"
          onSubmit={handleSubmit}
          className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          <Field
            label="From batch"
            hint="Leave blank for a standalone recovery"
            tooltip="Which batch produced this material. Naming one means the system checks that the contributing lots you list are ones the batch really consumed. Leave it blank to reclaim material outside any batch — from expired stock, say — where the lots are taken on trust."
          >
            <select
              name="recoveryBatch"
              className={inputClass}
              value={batchId}
              onChange={(event) => {
                setBatchId(event.target.value);
                setSourceLotIds([]);
              }}
            >
              <option value="">Standalone — not from a batch</option>
              {batches.data?.items
                .filter((batch) => batch.status !== 'cancelled')
                .map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.code} — {batch.itemCode} ({batch.status})
                  </option>
                ))}
            </select>
          </Field>

          <Field label="Material recovered">
            <select
              name="recoveryItem"
              className={inputClass}
              value={itemId}
              onChange={(event) => {
                setItemId(event.target.value);
                setSourceLotIds([]);
                setUom('');
                setReleasePolicy('');
                const item = items.data?.items.find(
                  (candidate) => candidate.id === event.target.value,
                );
                if (item) setUom(item.baseUom);
              }}
              required
            >
              <option value="">Select the material…</option>
              {items.data?.items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.code} — {item.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Put it in">
            <select
              name="recoveryLocation"
              className={inputClass}
              value={locationId}
              onChange={(event) => setLocationId(event.target.value)}
              required
            >
              <option value="">Select a location…</option>
              {locations.data?.items
                .filter((location) => location.isActive)
                .map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.code} — {location.name}
                  </option>
                ))}
            </select>
          </Field>

          <Field label="Quantity recovered">
            <div className="flex gap-2">
              <input
                name="recoveryQuantity"
                className={inputClass}
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                required
                inputMode="decimal"
                placeholder="18.4"
              />
              <input
                name="recoveryUom"
                className={`${inputClass} max-w-[5rem]`}
                value={uom}
                onChange={(event) => setUom(event.target.value)}
                required
                aria-label="Unit of measure"
              />
            </div>
          </Field>

          <Field
            label="Contributing lots"
            hint="The lots the material actually came from"
            tooltip="The specific lots this material was reclaimed from. They are what makes the recovered lot traceable back to real stock, so at least one is always required. Choosing a batch above narrows the list to what that batch actually used."
          >
            <MultiCheckboxList
              options={lotsInScope.map((lot) => ({
                value: lot.id,
                label: `${lot.code} (${lot.origin}, ${lot.status})`,
              }))}
              selected={sourceLotIds}
              onChange={setSourceLotIds}
              emptyLabel="No lots to choose from yet — pick the material above, or the batch it came from."
            />
          </Field>

          <Field
            label="Recovery step"
            hint="Which part of the process it came out of"
            tooltip="Where in the process the material was reclaimed from, for example “mother liquor” or “final wash”. It is free text and goes straight onto the recovered lot's ledger entry and trace."
          >
            <input
              name="recoveryStep"
              className={inputClass}
              value={recoveryStep}
              onChange={(event) => setRecoveryStep(event.target.value)}
              required
              placeholder="Distillation residue"
            />
          </Field>

          <Field label="Method" hint="Optional">
            <input
              name="recoveryMethod"
              className={inputClass}
              value={method}
              onChange={(event) => setMethod(event.target.value)}
              placeholder="Fractional distillation"
            />
          </Field>

          <Field label="Grade" hint="Optional">
            <input
              name="recoveryGrade"
              className={inputClass}
              value={grade}
              onChange={(event) => setGrade(event.target.value)}
            />
          </Field>

          <Field label="Purity %" hint="Optional">
            <input
              name="recoveryPurity"
              className={inputClass}
              value={purityPct}
              onChange={(event) => setPurityPct(event.target.value)}
              inputMode="decimal"
            />
          </Field>

          <Field
            label="Release policy"
            hint={
              selectedItem
                ? `Item default: ${defaultPolicy}`
                : 'Choose the material first'
            }
            tooltip="Whether this recovered material can be used straight away or has to wait for a quality check. Changing it from the item's default is allowed, but it needs a reason and is recorded."
          >
            <select
              name="recoveryPolicy"
              className={inputClass}
              value={releasePolicy}
              onChange={(event) => setReleasePolicy(event.target.value as '' | ReleasePolicy)}
            >
              <option value="">Use the item default</option>
              <option value="quarantine">Quarantine until QC releases it</option>
              <option value="immediate">Immediately usable</option>
            </select>
          </Field>

          <Field
            label="Credit unit cost"
            hint="Leave blank to use the item's credit policy"
            tooltip="What the recovered material is worth per unit. Left blank, the item's valuation policy prices it — usually as a share of what the contributing lots originally cost. Filling it in overrides that for this recovery only."
          >
            <input
              name="recoveryCredit"
              className={inputClass}
              value={creditedUnitCost}
              onChange={(event) => setCreditedUnitCost(event.target.value)}
              inputMode="decimal"
            />
          </Field>

          {isOverride ? (
            <div className="sm:col-span-2 lg:col-span-3">
              <Field label="Why override the policy?" hint="Required, and recorded in the audit trail">
                <input
                  name="recoveryOverrideReason"
                  className={inputClass}
                  value={policyOverrideReason}
                  onChange={(event) => setPolicyOverrideReason(event.target.value)}
                  placeholder="e.g. purity came back within spec and QC agreed"
                  required
                />
              </Field>
            </div>
          ) : null}

          <div className="sm:col-span-2 lg:col-span-3">
            <InfoNote>
              The recovered material becomes stock in <strong>{humanise(effectivePolicy)}</strong>{' '}
              state. It appears in inventory, valuation and traceability like any other lot — only
              the tag and its provenance are special.
            </InfoNote>
          </div>

          <div className="sm:col-span-2 lg:col-span-3">
            {record.error ? (
              <ErrorNote>
                {record.error instanceof ApiError
                  ? record.error.message
                  : 'Could not record the recovery.'}
              </ErrorNote>
            ) : null}
            <div className="mt-3 flex gap-2">
              <Button type="submit" disabled={record.isPending}>
                {record.isPending ? 'Recording…' : 'Record recovery'}
              </Button>
              <Button tone="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
